import { firstValueFrom, lastValueFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { type Injector, runInInjectionContext } from '@angular/core';
import { ActivityEntityName, SOR_EVIDENCE_BUCKETS, SOR_EVIDENCE_BUCKET_LABELS, SOR_EVIDENCE_TASK_FIELDS, TaskLinkType } from '@jms/model';
import type { Attachment, SorEvidenceBucket, TaskAddAttachmentsRequest, TaskAddAttachmentsResponse } from '@jms/model';
import { injectWorkBuddyHost } from '@workbuddy/services/host/workbuddy.host.service';
import { WorkBuddyDialogService } from '@workbuddy/ui/services';
import { SorTaskCountService } from '../services/item.task.count.service';
import type { ContractorBulkCommand, ContractorBulkOutcome } from './contractor.item.delete.bulk.action';
import { resolveContractorBulkLines, runContractorBulkLines, type ContractorBulkLine } from './contractor.item.bulk.tally';
import { buildJobItemBulkActionTitle } from './item.bulk.action.title';
import { ItemEvidenceBulkDialogComponent, type ItemEvidenceBulkDialogData, type ItemEvidenceBulkResult } from './item.evidence.dialog';

export const CONTRACTOR_ITEM_EVIDENCE_BULK_ACTION = 'items.contractor.evidence';

/**
 * The files uploaded once in the dialog go onto each line's SOR task through the portal task route, one line at a
 * time; a line whose task collects none of the filled buckets is refused with the reason the web bulk run gives.
 */
export async function handleContractorItemEvidenceBulkCommand(args: ContractorBulkCommand, injector: Injector, itemsChanged?: () => void): Promise<ContractorBulkOutcome> {

  if (args?.action !== CONTRACTOR_ITEM_EVIDENCE_BULK_ACTION) return { handled: false };

  const lines = resolveContractorBulkLines(args.data);
  if (!lines.length) return { handled: true };

  // No job library on the portal: the related-files picker reads a tenant files route this host does not proxy.
  const data: ItemEvidenceBulkDialogData = { title: buildJobItemBulkActionTitle('Add Photo / Video Evidence', args.data), job: null };
  const ref = injector.get(WorkBuddyDialogService).open(ItemEvidenceBulkDialogComponent, { data });
  const filled = (await firstValueFrom(ref.onClose)) as ItemEvidenceBulkResult | undefined;
  if (!filled || !Object.keys(filled).length) return { handled: true };

  const http = injector.get(HttpClient);
  const taskCounts = injector.get(SorTaskCountService);
  const taskApi = runInInjectionContext(injector, injectWorkBuddyHost).taskApi;

  await runContractorBulkLines(lines, injector, async line => {
    const task = (await taskCounts.getTasks(line.itemId)).find(candidate => candidate?.type === TaskLinkType.Item);
    if (!task?._id) return { kind: 'failed', reason: 'No SOR task' };

    const buckets = SOR_EVIDENCE_BUCKETS.filter(bucket => !!filled[bucket]?.length);
    const eligible = buckets.filter(bucket => task[`${bucket}Enabled`] === true);
    const labels = (list: readonly SorEvidenceBucket[]) => list.map(bucket => SOR_EVIDENCE_BUCKET_LABELS[bucket]).join(', ');
    if (!eligible.length) return { kind: 'failed', reason: `No ${labels(buckets)} evidence bucket` };

    let alreadyLinked = 0;
    let linkedAny = false;
    for (const bucket of eligible) {
      const request: TaskAddAttachmentsRequest = { id: task._id, field: SOR_EVIDENCE_TASK_FIELDS[bucket], attachments: stampLineAttachments(filled[bucket] ?? [], line) };
      const response = await lastValueFrom(http.post<TaskAddAttachmentsResponse>(`${taskApi}/attachments/add`, request));
      const bucketSkipped = response?.alreadyLinked ?? 0;
      alreadyLinked += bucketSkipped;
      // The server skips what that SOR already holds and drops repeats within the request, so a bucket linked something
      // unless it skipped every distinct upload it was sent.
      if (bucketSkipped < distinctUploads(request.attachments)) linkedAny = true;
    }
    if (!linkedAny) return { kind: 'unchanged', reason: 'Already on that SOR', alreadyLinked };

    const skipped = buckets.filter(bucket => !eligible.includes(bucket));
    return skipped.length ? { kind: 'updated', note: `${labels(skipped)} skipped — no such bucket`, alreadyLinked } : { kind: 'updated', alreadyLinked };
  }, itemsChanged, { noun: 'SOR(s)' });

  return { handled: true, clearSelection: true };
}

// Repeats of one `_id` in a request write once, as `partitionEvidenceLinks` drops them; an upload with no `_id` always writes.
function distinctUploads(attachments: Attachment[]): number {

  const ids = attachments.filter(attachment => attachment?._id != null).map(attachment => String(attachment._id));
  return new Set(ids).size + attachments.length - ids.length;
}

// The upload's `_id` is kept on every line, as the web bulk run does, so the Files tab lists one photo once.
function stampLineAttachments(attachments: Attachment[], line: ContractorBulkLine): Attachment[] {

  return attachments.map(attachment => ({ ...attachment, entityId: line.itemId, entityName: ActivityEntityName.Item, parentId: line.stageId }));
}
